import "../CodeEditor.css";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { StreamLanguage } from "@codemirror/language";
import { json } from "@codemirror/legacy-modes/mode/javascript";
import { yaml } from "@codemirror/legacy-modes/mode/yaml";
import ReactCodeMirror from "@uiw/react-codemirror";
import { Tree } from "antd";
import { lazy, useMemo, useCallback, Suspense } from "react";
import Button from "./Button.js";
import Icon from "./Icon.js";
import Message from "./Message.js";
import Spinner from "./Spinner.js";
import { useTheme } from "./Theme/UI.js";
import "./Theme/themeUtils.js";
import "./Theme/themes.js";
import { Loaded, Loadable, NotLoaded } from "./utils/loadable.js";
const codeEditorBase = "CodeEditor_codeEditorBase_62e3e";
const fileTree = "CodeEditor_fileTree_62e3e";
const fileDir = "CodeEditor_fileDir_62e3e";
const fileInfo = "CodeEditor_fileInfo_62e3e";
const buttonContainer = "CodeEditor_buttonContainer_62e3e";
const filePath = "CodeEditor_filePath_62e3e";
const fileDesc = "CodeEditor_fileDesc_62e3e";
const readOnly = "CodeEditor_readOnly_62e3e";
const buttonsContainer = "CodeEditor_buttonsContainer_62e3e";
const editor = "CodeEditor_editor_62e3e";
const editorMode = "CodeEditor_editorMode_62e3e";
const pageError = "CodeEditor_pageError_62e3e";
const hideElement = "CodeEditor_hideElement_62e3e";
const css = {
  codeEditorBase,
  fileTree,
  fileDir,
  fileInfo,
  buttonContainer,
  filePath,
  fileDesc,
  readOnly,
  buttonsContainer,
  editor,
  editorMode,
  pageError,
  hideElement
};
const JupyterRenderer = lazy(() => import("./CodeEditor/IpynbRenderer.js"));
const { DirectoryTree } = Tree;
const MARKDOWN_CONFIG = {
  autocompletion: false,
  foldGutter: false,
  highlightActiveLineGutter: false
};
const sortTree = (a, b) => {
  if (a.children)
    a.children.sort(sortTree);
  if (b.children)
    b.children.sort(sortTree);
  const titleA = String(a.title);
  const titleB = String(b.title);
  if (isConfig(a.key) && !isConfig(b.key))
    return -1;
  if (!isConfig(a.key) && isConfig(b.key))
    return 1;
  if (isConfig(a.key) && isConfig(b.key))
    return a.key < b.key ? 1 : -1;
  if (!a.isLeaf && b.isLeaf)
    return -1;
  if (a.isLeaf && !b.isLeaf)
    return 1;
  if (!a.isLeaf && !b.isLeaf)
    return titleA.localeCompare(titleB) - titleB.localeCompare(titleA);
  const [stringA, extensionA] = titleA.split(/^(?=[a-zA-Z])\./);
  const [stringB, extensionB] = titleB.split(/^(?=[a-zA-Z])\./);
  if (!extensionA && extensionB)
    return 1;
  if (!extensionB && extensionA)
    return -1;
  if (!extensionA && !extensionB)
    return stringA.localeCompare(stringB) - stringB.localeCompare(stringA);
  return extensionA.localeCompare(extensionB) - extensionB.localeCompare(extensionB);
};
const Config = {
  Runtime: "Runtime Configuration",
  Submitted: "Submitted Configuration"
};
const isConfig = (key) => key === Config.Submitted || key === Config.Runtime;
const langs = {
  json: () => StreamLanguage.define(json),
  markdown: () => markdown({ base: markdownLanguage }),
  python,
  yaml: () => StreamLanguage.define(yaml)
};
const CodeEditor = ({
  files,
  height = "100%",
  file,
  onChange,
  onError,
  onSelectFile,
  readonly,
  selectedFilePath = String(((_a) => (_a = files[0]) == null ? void 0 : _a.key)())
}) => {
  var _a2, _b;
  const loadableFile = useMemo(() => typeof file === "string" ? Loaded(file) : file, [file]);
  const sortedFiles = useMemo(() => [...files].sort(sortTree), [files]);
  const {
    themeSettings: { themeIsDark, className: themeClass }
  } = useTheme();
  const viewMode = useMemo(() => files.length === 1 ? "editor" : "split", [files.length]);
  const activeFile = useMemo(() => {
    if (viewMode === "editor") {
      return files[0];
    }
    if (!selectedFilePath) {
      return null;
    }
    const splitFilePath = selectedFilePath.split("/");
    let matchTopFileOrFolder = null;
    let fileDir2 = files;
    for (let dir = 0; dir < splitFilePath.length; dir++) {
      matchTopFileOrFolder = fileDir2.find(
        (f) => f.key === splitFilePath.slice(0, dir + 1).join("/") || f.key === selectedFilePath
      );
      if (matchTopFileOrFolder == null ? void 0 : matchTopFileOrFolder.children) {
        fileDir2 = matchTopFileOrFolder.children;
      }
    }
    return matchTopFileOrFolder;
  }, [files, selectedFilePath, viewMode]);
  const editorMode2 = useMemo(() => {
    const isIpybnFile = /\.ipynb$/i.test(String((activeFile == null ? void 0 : activeFile.key) || ""));
    return isIpybnFile ? "ipynb" : "codemirror";
  }, [activeFile == null ? void 0 : activeFile.key]);
  const syntax = useMemo(() => {
    if (String(activeFile == null ? void 0 : activeFile.key).includes(".py"))
      return "python";
    if (String(activeFile == null ? void 0 : activeFile.key).includes(".json"))
      return "json";
    if (String(activeFile == null ? void 0 : activeFile.key).includes(".md"))
      return "markdown";
    return "yaml";
  }, [activeFile == null ? void 0 : activeFile.key]);
  const handleSelectFile = useCallback(
    (_, info) => {
      var _a3;
      const selectedKey = String(info.node.key);
      if (selectedKey === (activeFile == null ? void 0 : activeFile.key)) {
        return;
      }
      const nodeAddress = selectedKey.split("/");
      let targetNode = files.find((node) => node.title === nodeAddress[0]);
      for (const dir of nodeAddress.slice(1))
        targetNode = (_a3 = targetNode == null ? void 0 : targetNode.children) == null ? void 0 : _a3.find((file2) => file2.title === dir);
      if (!targetNode) {
        return;
      }
      if (targetNode.isLeaf) {
        onSelectFile == null ? void 0 : onSelectFile(String(targetNode.key));
      }
    },
    [activeFile == null ? void 0 : activeFile.key, files, onSelectFile]
  );
  const handleDownloadClick = useCallback(() => {
    if (!Loadable.isLoadable(loadableFile) || !Loadable.isLoaded(loadableFile) || !activeFile)
      return;
    const link = document.createElement("a");
    link.download = isConfig(activeFile == null ? void 0 : activeFile.key) ? activeFile.download || "" : String(activeFile.title);
    link.href = URL.createObjectURL(new Blob([Loadable.getOrElse("", loadableFile)]));
    link.dispatchEvent(new MouseEvent("click"));
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
    }, 2e3);
  }, [activeFile, loadableFile]);
  const classes = [
    css.codeEditorBase,
    loadableFile.isFailed ? css.noEditor : "",
    viewMode === "editor" ? css.editorMode : "",
    themeClass
  ];
  const sectionClasses = [loadableFile.isFailed ? css.pageError : css.editor];
  const treeClasses = [css.fileTree, viewMode === "editor" ? css.hideElement : ""];
  let fileContent = /* @__PURE__ */ jsx("h5", { children: "Please, choose a file to preview." });
  if (loadableFile.isFailed) {
    fileContent = /* @__PURE__ */ jsx(Message, { icon: "error", title: ((_a2 = loadableFile.error) == null ? void 0 : _a2.message) ?? "Unknown Error" });
  } else if (activeFile) {
    fileContent = editorMode2 === "codemirror" ? /* @__PURE__ */ jsx(
      ReactCodeMirror,
      {
        basicSetup: syntax === "markdown" ? MARKDOWN_CONFIG : void 0,
        extensions: [langs[syntax]()],
        height: "100%",
        readOnly: readonly,
        style: { height: "100%" },
        theme: themeIsDark ? "dark" : "light",
        value: Loadable.getOrElse("", loadableFile),
        onChange
      }
    ) : /* @__PURE__ */ jsx(Suspense, { fallback: /* @__PURE__ */ jsx(Spinner, { spinning: true, tip: "Loading ipynb viewer..." }), children: /* @__PURE__ */ jsx(JupyterRenderer, { file: Loadable.getOrElse("", loadableFile), onError }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: classes.join(" "), style: { height }, children: [
    /* @__PURE__ */ jsx(
      DirectoryTree,
      {
        className: treeClasses.join(" "),
        "data-testid": "fileTree",
        defaultExpandAll: true,
        defaultSelectedKeys: [selectedFilePath || ((_b = sortedFiles[0]) == null ? void 0 : _b.key)],
        treeData: sortedFiles,
        onSelect: handleSelectFile
      }
    ),
    !!(activeFile == null ? void 0 : activeFile.title) && /* @__PURE__ */ jsx("div", { className: css.fileDir, children: /* @__PURE__ */ jsxs("div", { className: css.fileInfo, children: [
      /* @__PURE__ */ jsx("div", { className: css.buttonContainer, children: /* @__PURE__ */ jsxs(Fragment, { children: [
        activeFile.icon ?? /* @__PURE__ */ jsx(Icon, { decorative: true, name: "document" }),
        /* @__PURE__ */ jsx("span", { className: css.filePath, children: /* @__PURE__ */ jsx(Fragment, { children: activeFile.title }) }),
        (activeFile == null ? void 0 : activeFile.subtitle) && /* @__PURE__ */ jsxs("span", { className: css.fileDesc, children: [
          " ",
          activeFile == null ? void 0 : activeFile.subtitle
        ] }),
        readonly && /* @__PURE__ */ jsx("span", { className: css.readOnly, children: "read-only" })
      ] }) }),
      /* @__PURE__ */ jsx("div", { className: css.buttonsContainer, children: readonly && file !== NotLoaded && /* @__PURE__ */ jsx(
        Button,
        {
          icon: /* @__PURE__ */ jsx(Icon, { name: "download", showTooltip: true, size: "small", title: "Download File" }),
          type: "text",
          onClick: handleDownloadClick
        }
      ) })
    ] }) }),
    /* @__PURE__ */ jsx("div", { className: sectionClasses.join(" "), children: /* @__PURE__ */ jsx(Spinner, { spinning: file === NotLoaded, children: fileContent }) })
  ] });
};
export {
  CodeEditor as default
};
